import TiktokIcon from '@/components/icons/tiktok-icon';
import { FooterElementDataType } from '@/components/sections/builder/elements/footer/footer/types';
import { AnnouncementElementDataType } from '@/components/sections/builder/elements/header/announcement/types';
import { ElementProps } from '@/components/sections/builder/types';
import { WrapperChild } from '@/components/sections/builder/wrapper';
import { Carousel, CarouselContent, CarouselItem } from '@/components/ui/carousel';
import { cn } from '@/lib/utils';
import { useBuilderStore } from '@/stores/builder';
import clsx from 'clsx';
import Autoplay from 'embla-carousel-autoplay';
import { FacebookIcon, InstagramIcon, TwitterIcon, YoutubeIcon } from 'lucide-react';
import Link from 'next/link';
import { shallow } from 'zustand/shallow';

export default function ElementContent({ element, preview }: ElementProps) {
  const childs = element.childs;

  const data = element.data as AnnouncementElementDataType;

  const builder = useBuilderStore(
    (state) => ({
      elements: state.elements,
      setElementData: state.setElementData,
    }),
    shallow
  );
  const footerElements = builder.elements?.find((e) => e.group === 'footer');
  const footerData = footerElements?.data as FooterElementDataType;

  const isShowSocialMedia =
    data.showSocialMediaIcon &&
    (footerData?.twitterUrl ||
      footerData?.facebookUrl ||
      footerData?.instagramUrl ||
      footerData?.youtubeUrl ||
      footerData?.tiktokUrl);

  return (
    <>
      <div className='w-full' id='announcement' data-testid='announcement'>
        <div className={clsx('announcement flex w-full flex-wrap gap-x-2  px-3 py-3')}>
          {isShowSocialMedia && (
            <div
              className={cn('flex items-center ', {
                'order-first': data.socialIconAlign === 'left',
                'order-last': data.socialIconAlign === 'right',
              })}
            >
              <span className='hidden space-x-3 lg:flex '>
                {footerData.twitterUrl && (
                  <Link href={footerData.twitterUrl}>
                    <TwitterIcon width={18} height={18} />
                  </Link>
                )}
                {footerData.facebookUrl && (
                  <Link href={footerData.facebookUrl}>
                    <FacebookIcon width={18} height={18} />
                  </Link>
                )}
                {footerData.instagramUrl && (
                  <Link href={footerData.instagramUrl}>
                    <InstagramIcon width={18} height={18} />
                  </Link>
                )}
                {footerData.youtubeUrl && (
                  <Link href={footerData.youtubeUrl}>
                    <YoutubeIcon width={19} height={19} />
                  </Link>
                )}
                {footerData.tiktokUrl && (
                  <Link href={footerData.tiktokUrl}>
                    <TiktokIcon
                      width={20}
                      height={20}
                      color={data.colorScheme?.text || '#52535B'}
                    />
                  </Link>
                )}
              </span>
            </div>
          )}
          <div
            className={clsx(
              'flex flex-1',
              data.contentAlign === 'left' && ' justify-center lg:justify-start',
              data.contentAlign === 'center' && 'justify-center',
              data.contentAlign === 'right' && ' justify-center lg:justify-end'
            )}
          >
            <Carousel
              plugins={
                data.changeEvery
                  ? [Autoplay({ playOnInit: true, delay: data.changeEvery * 1000 })]
                  : []
              }
              className={cn('max-w-[80%]', isShowSocialMedia && 'lg:w-3/5')}
              opts={{ loop: true }}
            >
              <CarouselContent className='px-2'>
                {(childs || []).map((child) => (
                  <CarouselItem key={child.id}>
                    <WrapperChild
                      child={{
                        ...child,
                        parentData: data,
                      }}
                      parentType={element.type}
                      preview={preview}
                    />
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
          </div>

          {isShowSocialMedia && <div className=''> </div>}
        </div>
      </div>

      <style jsx>
        {`
          .announcement {
            padding-top: ${data.padding?.top}px;
            padding-bottom: ${data.padding?.bottom}px;
            padding-left: ${data.padding?.left}px;
            padding-right: ${data.padding?.right}px;
            background-color: ${data.colorScheme?.background || 'white'};
            color: ${data.colorScheme?.text || '#52535B'};
          }
        `}
      </style>
    </>
  );
}
